import '@shared/ui/theme/global.css';

import { initI18n } from '@shared/ui/i18n/i18n.config';
import { QueryProvider } from '@shared/ui/query/query-provider.component';
import { createBrandColours } from '@shared/ui/theme/branding';
import { prePaintScript } from '@shared/ui/theme/theme';
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import en from './locales/en/common.json';

export { ErrorBoundary } from '@shared/ui/error/error-boundary.component';

// Bundle en so SSR + first client paint agree (no hydration mismatch).
initI18n({ en: { common: en } });

// Each app sets its own brand primary.
const brand = createBrandColours('#7c3aed') as CSSProperties;

export function Layout({ children }: { children: ReactNode }) {
	// suppressHydrationWarning: the pre-paint script sets data-theme on <html>
	// before React hydrates, so its attributes intentionally differ from SSR.
	return (
		<html lang="en" style={brand} suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<link rel="icon" href="/favicon.svg" type="image/svg+xml" />
				{/* Resolve the theme before first paint to avoid a flash. */}
				<script dangerouslySetInnerHTML={{ __html: prePaintScript }} />
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	// Mark the document hydrated once the client has mounted — a signal e2e tests
	// wait on before interacting, so they never click a not-yet-hydrated control.
	useEffect(() => {
		document.documentElement.dataset.hydrated = 'true';
	}, []);

	// Global onError: the one place a failed query/mutation surfaces. No toast
	// system in the demo yet, so it logs — a real app would show a notice here.
	return (
		<QueryProvider onError={(error) => console.error(error)}>
			<Outlet />
		</QueryProvider>
	);
}
